import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useEntityMedia } from "@/hooks/use-api";
import { OVERVIEW_THUMB } from "@/lib/images";
import { JobPhoto } from "./job-photo";
import { PhotoLightbox } from "./photo-lightbox";
import { groupPhotosByStage, imageItemsFromSubmissions } from "./media-helpers";
import type { MediaItem } from "@adva/types";

// How many tiles the strip shows. When the total exceeds this, the last tile
// becomes a "+N" overflow tile so the row footprint never grows (§7).
const MAX_TILES = 4;

/** Sort a copy of the items most-recent-first (§2.2 — Overview answers "what's
 * happening now"; the stage narrative lives in the full gallery). */
function recentFirst(items: MediaItem[]): MediaItem[] {
  return [...items].sort((a, b) =>
    a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
  );
}

/**
 * Presentational Overview preview strip — a fixed one-row footprint (≈110px)
 * that previews an entity's photos without leaving the Overview tab. Split from
 * the data container so the layout is unit-testable without a QueryClient.
 *
 * Anatomy (spec §7): header `PHOTOS · N` + ghost "View all photos" → Photos
 * tab; up to 4 recent-first thumbs at 120×80. Each thumb opens the SHARED
 * lightbox at the photo's canonical (stage-grouped) flat index — so lightbox
 * navigation matches the gallery tab. When the total exceeds 4 the 4th slot is
 * a solid `--color-elevated` "+N" tile (no scrim over a photo, §I3.6) that also
 * routes to the Photos tab. Renders nothing at count 0 (§I3.1) — the strip is
 * additive, never an empty card.
 */
export function PhotoPreviewStripView({
  items,
  onViewAll,
}: {
  items: MediaItem[];
  /** Switch the detail page to its Photos tab. */
  onViewAll: () => void;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Canonical lightbox order (stage-grouped) — the strip displays recent-first
  // but the lightbox paginates the same flat array the gallery tab uses, so a
  // photo opened from either surface lands at the same index (§7).
  const { flat } = useMemo(() => groupPhotosByStage(items), [items]);
  const recent = useMemo(() => recentFirst(items), [items]);

  const total = items.length;
  if (total === 0) return null;

  const overflow = total > MAX_TILES;
  // With overflow we keep MAX_TILES-1 thumbs + the "+N" tile; otherwise show all.
  const thumbs = overflow ? recent.slice(0, MAX_TILES - 1) : recent;
  const remaining = total - thumbs.length;

  const openAt = (photo: MediaItem) => {
    const idx = flat.findIndex((p) => p.id === photo.id);
    if (idx >= 0) setLightboxIndex(idx);
  };

  return (
    <TooltipProvider delayDuration={150}>
      <section aria-label="Photos preview">
        <div className="flex items-center justify-between">
          <h3 className="typo-label text-[color:var(--color-text-md)]">
            Photos
            <span className="text-[color:var(--color-text-lo)]">
              {" · "}
              {total}
            </span>
          </h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onViewAll}
            className="h-auto px-2 py-1 text-[color:var(--color-text-md)]"
          >
            View all photos
          </Button>
        </div>

        <div className="mt-2 flex gap-2">
          {thumbs.map((photo) => (
            <JobPhoto
              key={photo.id}
              item={photo}
              slot={OVERVIEW_THUMB}
              hideCaption
              onOpen={() => openAt(photo)}
              className="w-[120px] flex-none"
            />
          ))}

          {overflow && (
            <button
              type="button"
              onClick={onViewAll}
              aria-label={`View all ${total} photos`}
              className="group flex w-[120px] flex-none items-center justify-center rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-elevated)] aspect-[3/2] transition-shadow duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] hover:ring-2 hover:ring-[color:var(--color-accent)]"
            >
              <span className="typo-body font-medium text-[color:var(--color-text-md)]">
                +{remaining}
              </span>
            </button>
          )}
        </div>
      </section>

      <PhotoLightbox
        photos={flat}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
      />
    </TooltipProvider>
  );
}

/** Data container — fetches an entity's media and renders the preview strip.
 * Uses the same `useEntityMedia` selector as the gallery tab (job → by-entity,
 * opportunity → cross-child rollup), so both surfaces show the same photo set. */
export function PhotoPreviewStrip({
  entityType,
  entityId,
  onViewAll,
}: {
  entityType: "job" | "opportunity";
  entityId: string;
  onViewAll: () => void;
}) {
  const { data } = useEntityMedia(entityType, entityId);
  const items = useMemo(
    () => imageItemsFromSubmissions(data?.submissions),
    [data],
  );
  return <PhotoPreviewStripView items={items} onViewAll={onViewAll} />;
}
